/* global HTMLDivElement, KeyboardEvent */
// Universal create/edit modal for every domain entity.
// Driven by FIELD config (entity-form-config); submit dispatches to the matching
// add/update action via the pure helpers in entity-form-payload.
// Event kind: extended with endTime, durationMin, rrule, exdates (D8/D9).
// Project kind: extended with goalId (D2 vertical link).
// Account kind: D4 wallet/account CRUD (name, type, openingBalance, currency default VND).
// No-orphan upgrades: task→project/goal, habit→goal, note→category+project, budget→rollover.
import { useEffect, useRef, useState } from "react";
import { X } from "lucide-react";
import type { LifeOsApi } from "../lib/life-os-contract";
import { DEFAULT_ACCOUNT_ID } from "../lib/storage";
import { useFocusTrap } from "../lib/focus-trap";
import { BentoColorInput, BentoDateInput, BentoMoneyInput, BentoSelect, BentoTimeInput } from "./bento-ui";
import {
  EventRecurrenceFields,
  EventReminderFields,
  rruleToState,
  defaultRecurrenceState,
} from "./event-recurrence-fields";
import type { RecurrenceState } from "./event-recurrence-fields";
import { CONFIG } from "./entity-form-config";
import type { EntityKind, Field } from "./entity-form-config";
import { initialValues, buildPayload, addDispatch, updateDispatch } from "./entity-form-payload";
import type { FormTarget } from "./entity-form-payload";
import { CategorySelect, categoryOptionsFor } from "./category-select";
import { MarkdownTextarea } from "./markdown-textarea";
import { DurationInput } from "./duration-input";
import { InfoHint } from "./info-hint";

// Re-exported so existing importers keep resolving these from entity-form.
export type { EntityKind, FormTarget };

export function EntityFormModal({ target, api, onClose, onSaved }: { target: FormTarget; api: LifeOsApi; onClose: () => void; onSaved?: (msg: string) => void }) {
  const panelRef = useRef<HTMLDivElement>(null);
  useFocusTrap(panelRef, Boolean(target));

  // Escape closes the modal
  useEffect(() => {
    if (!target) return;
    const handleKeyDown = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [target, onClose]);

  const [values, setValues] = useState<Record<string, unknown>>({});
  // Separate recurrence state for the event form's RRULE builder
  const [recurrence, setRecurrence] = useState<RecurrenceState>(defaultRecurrenceState());
  // D10: reminder offsets (minutes before) for the event form
  const [reminders, setReminders] = useState<number[]>([]);

  useEffect(() => {
    if (target) {
      const init = initialValues(target);
      // D4: Pre-fill accountId for new transactions/recurring — first account or DEFAULT
      if ((target.kind === "transaction" || target.kind === "recurring") && !target.item?.accountId) {
        init.accountId = api.state.accounts[0]?.id ?? DEFAULT_ACCOUNT_ID;
      }
      setValues(init);
      // Restore recurrence + reminders state when editing an existing event or task (D27)
      if (target.kind === "event") {
        setRecurrence(rruleToState(target.item?.rrule as string | undefined));
        setReminders(Array.isArray(target.item?.reminders) ? (target.item!.reminders as number[]) : []);
      } else if (target.kind === "task") {
        // Task recurrence: restore from task.recurrence (RRULE string); instances have no editor
        setRecurrence(rruleToState(target.item?.recurrence as string | undefined));
        setReminders([]);
      } else {
        setRecurrence(defaultRecurrenceState());
        setReminders([]);
      }
    }
    // Depend ONLY on `target` (form open/switch). Do NOT depend on api.state.accounts:
    // every cloud sync snapshot yields a new accounts array reference, which would
    // re-run this effect and wipe whatever the user is typing in a synced session.
    // The accountId pre-fill reads the latest accounts at open time, which is sufficient.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target]);

  if (!target) return null;

  const cfg = CONFIG[target.kind];
  const editing = Boolean(target.item?.id);
  // D43: danh mục từ danh sách đóng financeCategories, lọc theo loại giao dịch
  // (giao dịch: theo values.type; ngân sách: luôn là danh mục chi tiêu).
  const categoryOptions =
    target.kind === "transaction"
      ? categoryOptionsFor(api.state, values.type === "income" ? "income" : "expense")
      : target.kind === "budget"
        ? categoryOptionsFor(api.state, "expense")
        : [];
  const set = (name: string, value: unknown) => setValues((v) => ({ ...v, [name]: value }));
  // P6-4: note titles for the markdown body's [[ backlink autocomplete.
  const noteTitles = api.state.notes.map((n) => n.title).filter(Boolean);

  // D49: ẩn field định kỳ không liên quan theo tần suất đang chọn — bớt rối.
  //  - Hằng tuần: bỏ "ngày trong tháng" + "ngày cuối tháng" (không có khái niệm ngày tháng).
  //  - Bật "ngày cuối tháng": ô "ngày trong tháng" thừa.
  const isHidden = (f: Field): boolean => {
    if (target.kind !== "recurring") return false;
    if (f.name === "dayOfMonth") return values.frequency === "weekly" || Boolean(values.endOfMonth);
    if (f.name === "endOfMonth") return values.frequency === "weekly";
    return false;
  };

  const submit = () => {
    const payload = buildPayload(target.kind, values, recurrence, reminders);
    if (editing) updateDispatch(api, target.kind, String(target.item!.id), payload);
    else addDispatch(api, target.kind, payload);
    onSaved?.(editing ? "Đã lưu thay đổi" : `Đã tạo ${cfg.title}`);
    onClose();
  };
  const requiredOk = cfg.fields.filter((f) => f.required).every((f) => String(values[f.name] ?? "").trim() !== "");
  // Ràng buộc min/max (vd ngày định kỳ 1–28): vi phạm CHẶN nút + báo rõ,
  // không kẹp âm thầm lúc lưu nữa.
  const rangeViolation = cfg.fields.find((f) => {
    if (isHidden(f)) return false; // field ẩn (vd ngày tháng khi Hằng tuần) không chặn submit
    if (f.min === undefined && f.max === undefined) return undefined;
    const raw = String(values[f.name] ?? "").trim();
    if (!raw) return false;
    const n = Number(raw);
    return Number.isNaN(n) || (f.min !== undefined && n < f.min) || (f.max !== undefined && n > f.max);
  });
  // P6.3: events must end after they start (no negative-duration "09:00–08:00").
  const t = String(values.time ?? "");
  const et = String(values.endTime ?? "");
  const timeOrderError = target.kind === "event" && t !== "" && et !== "" && et <= t;
  const ok = requiredOk && !rangeViolation && !timeOrderError;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-ink-900/40 p-4 backdrop-blur-sm" onClick={onClose} role="dialog" aria-modal="true" aria-labelledby="entity-form-title">
      <div ref={panelRef} className="fade-in max-h-[88vh] w-full max-w-md overflow-auto rounded-3xl bg-white p-6" onClick={(e) => e.stopPropagation()}>
        <div className="mb-4 flex items-center justify-between">
          <h3 id="entity-form-title" className="font-display text-[18px] font-bold">{editing ? "Sửa" : "Tạo"} {cfg.title}</h3>
          <button onClick={onClose} aria-label="Đóng" className="grid h-8 w-8 place-items-center rounded-lg text-ink-500 hover:bg-ink-200/60"><X className="h-4 w-4" /></button>
        </div>
        <div className="grid grid-cols-2 gap-3">
          {cfg.fields.filter((f) => !isHidden(f)).map((f) => (
            <label key={f.name} className={`block ${f.span || f.type === "textarea" || f.type === "markdown" ? "col-span-2" : ""}`}>
              <span className="mb-1 block text-[12px] font-semibold text-ink-500">{f.label}</span>
              <FieldInput field={f} value={values[f.name]} onChange={(val) => set(f.name, val)} categoryOptions={categoryOptions} noteTitles={noteTitles} />
            </label>
          ))}
          {/* D2: Goal selector — only for project kind */}
          {target.kind === "project" && (
            <label className="col-span-2 block">
              <span className="mb-1 block text-[12px] font-semibold text-ink-500">Mục tiêu liên kết</span>
              <BentoSelect
                className="py-2.5 text-[14px]"
                value={String(values.goalId ?? "")}
                onChange={(e) => set("goalId", e.target.value)}
              >
                <option value="">— Không —</option>
                {api.state.goals.map((g) => <option key={g.id} value={g.id}>{g.title}</option>)}
              </BentoSelect>
            </label>
          )}
          {/* D4: Account selector — transaction + recurring (UX report: khoản định kỳ cần gắn ví) */}
          {(target.kind === "transaction" || target.kind === "recurring") && api.state.accounts.length > 0 && (
            <label className="col-span-2 block">
              <span className="mb-1 block text-[12px] font-semibold text-ink-500">Ví / Tài khoản</span>
              <BentoSelect
                className="py-2.5 text-[14px]"
                value={String(values.accountId ?? DEFAULT_ACCOUNT_ID)}
                onChange={(e) => set("accountId", e.target.value)}
              >
                {api.state.accounts.map((acc) => (
                  <option key={acc.id} value={acc.id}>{acc.name}</option>
                ))}
              </BentoSelect>
            </label>
          )}
          {/* No-orphan: Task → Project + Goal selects (sourced from api.state) */}
          {target.kind === "task" && (
            <>
              <label className="block">
                <span className="mb-1 block text-[12px] font-semibold text-ink-500">Dự án</span>
                <BentoSelect
                  className="py-2.5 text-[14px]"
                  value={String(values.projectId ?? "")}
                  onChange={(e) => set("projectId", e.target.value)}
                >
                  <option value="">— Không gắn —</option>
                  {api.state.projects.filter((p) => p.status !== "done").map((p) => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
                </BentoSelect>
              </label>
              <label className="block">
                <span className="mb-1 block text-[12px] font-semibold text-ink-500">Mục tiêu</span>
                <BentoSelect
                  className="py-2.5 text-[14px]"
                  value={String(values.goalId ?? "")}
                  onChange={(e) => set("goalId", e.target.value)}
                >
                  <option value="">— Không gắn —</option>
                  {api.state.goals.map((g) => (
                    <option key={g.id} value={g.id}>{g.title}</option>
                  ))}
                </BentoSelect>
              </label>
            </>
          )}
          {/* No-orphan: Habit → Goal (D14 linkedGoalId) */}
          {target.kind === "habit" && (
            <label className="col-span-2 block">
              <span className="mb-1 block text-[12px] font-semibold text-ink-500">Gắn vào mục tiêu</span>
              <BentoSelect
                className="py-2.5 text-[14px]"
                value={String(values.linkedGoalId ?? "")}
                onChange={(e) => set("linkedGoalId", e.target.value)}
              >
                <option value="">— Không gắn —</option>
                {api.state.goals.map((g) => (
                  <option key={g.id} value={g.id}>{g.title}</option>
                ))}
              </BentoSelect>
            </label>
          )}
          {/* No-orphan: Note → PARA category + Project (D16) */}
          {target.kind === "note" && (
            <>
              <label className="block">
                <span className="mb-1 flex items-center gap-1 text-[12px] font-semibold text-ink-500">
                  Phân loại PARA
                  <InfoHint label="PARA là gì?" text="PARA = Projects (dự án có hạn) · Areas (mảng cần duy trì) · Resources (tư liệu tham khảo) · Archive (lưu trữ). Cách sắp xếp ghi chú theo mức độ hành động." />
                </span>
                <BentoSelect
                  className="py-2.5 text-[14px]"
                  value={String(values.category ?? "")}
                  onChange={(e) => set("category", e.target.value)}
                >
                  <option value="">— Không phân loại —</option>
                  <option value="project">Dự án</option>
                  <option value="area">Khu vực</option>
                  <option value="resource">Tư liệu</option>
                  <option value="archive">Lưu trữ</option>
                </BentoSelect>
              </label>
              <label className="block">
                <span className="mb-1 block text-[12px] font-semibold text-ink-500">Gắn vào dự án</span>
                <BentoSelect
                  className="py-2.5 text-[14px]"
                  value={String(values.linkedProjectId ?? "")}
                  onChange={(e) => set("linkedProjectId", e.target.value)}
                >
                  <option value="">— Không gắn —</option>
                  {api.state.projects.filter((p) => p.status !== "done").map((p) => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
                </BentoSelect>
              </label>
            </>
          )}
          {/* Recurrence builder + reminders — only for event kind */}
          {target.kind === "event" && (
            <div className="col-span-2 space-y-3">
              <EventRecurrenceFields value={recurrence} onChange={setRecurrence} />
              <EventReminderFields reminders={reminders} onChange={setReminders} />
            </div>
          )}
          {/* D27: Recurrence editor — task series parent only.
              Generated instances (recurrenceParentId set) show read-only notice instead. */}
          {target.kind === "task" && Boolean(target.item?.recurrenceParentId) && (
            <div className="col-span-2 rounded-2xl border border-ink-200 bg-ink-50 px-3 py-2.5 text-[13px] text-ink-500">
              Thuộc chuỗi lặp lại · Sửa lịch trên nhiệm vụ gốc để thay đổi toàn bộ chuỗi.
            </div>
          )}
          {target.kind === "task" && !target.item?.recurrenceParentId && (
            <div className="col-span-2">
              <EventRecurrenceFields value={recurrence} onChange={setRecurrence} />
            </div>
          )}
        </div>
        {!ok && (
          <p className="mt-3 text-center text-[12px] font-semibold text-amber-600">
            {timeOrderError
              ? "Giờ kết thúc phải sau giờ bắt đầu."
              : rangeViolation
              ? `${rangeViolation.label.replace(/\s*\(.*\)$/, "")} phải từ ${rangeViolation.min ?? "…"} đến ${rangeViolation.max ?? "…"}`
              : "Vui lòng điền các trường bắt buộc"}
          </p>
        )}
        <div className="mt-3 grid grid-cols-2 gap-3">
          <button onClick={onClose} className="rounded-xl bg-ink-200/60 py-3 text-[14px] font-bold text-ink-700">Huỷ</button>
          <button onClick={submit} disabled={!ok} className="gbrand rounded-xl py-3 text-[14px] font-bold text-white disabled:opacity-50">{editing ? "Lưu" : "Tạo"}</button>
        </div>
      </div>
    </div>
  );
}

function FieldInput({ field, value, onChange, categoryOptions = [], noteTitles = [] }: { field: Field; value: unknown; onChange: (v: unknown) => void; categoryOptions?: string[]; noteTitles?: string[] }) {
  const base = "w-full rounded-xl border border-ink-200 bg-white px-3 py-2.5 text-[14px] outline-none focus:border-brand-500";
  if (field.type === "category") return <CategorySelect value={String(value ?? "")} onChange={onChange} options={categoryOptions} />;
  // "money": format nghìn + chặn số 0 đứng đầu — đồng bộ với quick-add (UX report P1).
  if (field.type === "money") return <BentoMoneyInput value={value} onChange={onChange} />;
  if (field.type === "duration") return <DurationInput value={Number(value) || 0} onChange={(n) => onChange(n)} max={field.max ?? 1440} className={base} />;
  if (field.type === "textarea") return <textarea rows={3} className={base} value={String(value ?? "")} onChange={(e) => onChange(e.target.value)} />;
  if (field.type === "markdown") return <MarkdownTextarea value={String(value ?? "")} onChange={(v) => onChange(v)} suggestions={noteTitles} ariaLabel={field.label} />;
  if (field.type === "select") return <BentoSelect className="py-2.5 text-[14px]" value={String(value ?? "")} onChange={(e) => onChange(e.target.value)}>{field.options!.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}</BentoSelect>;
  if (field.type === "date") return <BentoDateInput className="py-2.5 text-[14px]" value={String(value ?? "")} onChange={(e) => onChange(e.target.value)} />;
  if (field.type === "time") return <BentoTimeInput className="py-2.5 text-[14px]" value={String(value ?? "")} onChange={(e) => onChange(e.target.value)} />;
  if (field.type === "checkbox") return <label className="flex items-center gap-2 py-2 text-[14px]"><input type="checkbox" checked={Boolean(value)} onChange={(e) => onChange(e.target.checked)} className="h-4 w-4 accent-brand-500" /><span>Bật</span></label>;
  if (field.type === "color") return <BentoColorInput className="py-2.5 text-[14px]" value={String(value ?? "#7c3aed")} onChange={(e) => onChange(e.target.value)} />;
  return <input type={field.type} min={field.min} max={field.max} className={base} value={String(value ?? "")} onChange={(e) => onChange(e.target.value)} />;
}
